import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TenderSubCatName, TenderBlockSize, TenderBoxMode } from 'src/models/procurements/Tender/types';
import { isNotUndefined } from 'src/helpers';
import { Translations } from 'src/lib/I18nService/types';
import { TenderBox, TenderSubCat } from 'src/shared';
import { useTenderFree } from 'src/models/procurements/TenderFree/hooks';
import { BoxFieldArray, BoxFieldString, TenderBoxFactory } from 'src/models/procurements';

export const ContactSubCat: FC = () => {
  const { t: tenderT } = useTranslation(Translations.procurements);
  const { data } = useTenderFree();
  const subCat = useMemo(
    () => (data ? { ...data.generalCat[TenderSubCatName.Contact], size: TenderBlockSize.Half } : undefined),
    [data]
  );
  const buyerIdBox = useMemo(
    () =>
      subCat?.buyerId
        ? new TenderBoxFactory().create('buyerBoxes', [new BoxFieldString(subCat.buyerId, subCat.buyerId)])
        : undefined,
    [subCat?.buyerId]
  );
  const buyerNameBox = useMemo(
    () =>
      subCat?.buyerName
        ? new TenderBoxFactory().create('buyerBoxes', [new BoxFieldString(subCat.buyerName, subCat.buyerName)])
        : undefined,
    [subCat?.buyerName]
  );
  const personBox = useMemo(
    () =>
      subCat?.contactPerson
        ? new TenderBoxFactory().create('contactPersonBoxes', [
            new BoxFieldString(subCat.contactPerson, subCat.contactPerson)
          ])
        : undefined,
    [subCat?.contactPerson]
  );
  const phoneBox = useMemo(
    () =>
      subCat?.phone
        ? new TenderBoxFactory().create('phoneBoxes', [new BoxFieldString(subCat.phone, subCat.phone)])
        : undefined,
    [subCat?.phone]
  );
  const emailBox = useMemo(
    () =>
      subCat?.email
        ? new TenderBoxFactory().create('emailBoxes', [new BoxFieldString(subCat.email, subCat.email)])
        : undefined,
    [subCat?.email]
  );
  const postalCodeBox = useMemo(
    () =>
      subCat?.postcode
        ? new TenderBoxFactory().create('postcode', [new BoxFieldString(subCat.postcode, subCat.postcode)])
        : undefined,
    [subCat?.postcode]
  );
  const cityBox = useMemo(
    () =>
      subCat?.city ? new TenderBoxFactory().create('city', [new BoxFieldString(subCat.city, subCat.city)]) : undefined,
    [subCat?.city]
  );
  const nutsBox = useMemo(
    () =>
      subCat?.buyerNutsCode && !!subCat.buyerNutsCode.length
        ? new TenderBoxFactory().create('buyerNutsCode', [new BoxFieldArray('', subCat.buyerNutsCode)])
        : undefined,
    [subCat?.buyerNutsCode]
  );
  const addressBox = useMemo(
    () =>
      subCat?.postalAddress
        ? new TenderBoxFactory().create('postalAddress', [
            new BoxFieldString(subCat.postalAddress, subCat.postalAddress)
          ])
        : undefined,
    [subCat?.postalAddress]
  );

  const addressBoxes = useMemo(
    () => [addressBox, postalCodeBox, cityBox, nutsBox].filter(isNotUndefined),
    [addressBox, cityBox, nutsBox, postalCodeBox]
  );

  const contactBoxes = useMemo(
    () => [personBox, phoneBox, emailBox].filter(isNotUndefined),
    [emailBox, personBox, phoneBox]
  );

  return (
    <>
      {subCat && (
        <TenderSubCat subCat={subCat} boxTheme={false}>
          <TenderBox
            boxes={contactBoxes}
            title={tenderT('Contact person', { context: 'short' })}
            as={TenderBoxMode.Collection}
            size={TenderBlockSize.Half}
            actions={false}
            isFirstField
          />
          <TenderBox
            box={buyerNameBox}
            title={tenderT('Procuring agency')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Half}
            actions={false}
            isFirstField
          />
          <TenderBox
            boxes={addressBoxes}
            title={tenderT('Postal address')}
            as={TenderBoxMode.Collection}
            size={TenderBlockSize.Half}
            actions={false}
            isFirstField
          />
          <TenderBox
            box={buyerIdBox}
            title={tenderT("Procuring agency's national ID", { context: 'short' })}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Half}
            actions={false}
          />
        </TenderSubCat>
      )}
    </>
  );
};

export default ContactSubCat;
